import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useGetClientRowQuery } from '@/features/clients/clientsApi';
import { useGetInsightPoliciesInfiniteQuery } from '@/features/policies/policiesApi';
import { rowLimits } from '@/components/table/data';
import InfiniteTablePagination from '@/components/tables/InfiniteTablePagination';
import { columns } from './data';
import {
	NoRowsErrorOverlay,
	NoRowsOverlay,
} from '@/components/table/DataGridComponents/NoRowsOverlay';
import { usePoliciesSelect } from '@/context/policiesContext';
import { getComparator, stableSort } from '@/components/table/functions';

export const SelectPolicyTable = ({ clientId, statusId, typeId }) => {
	const theme = useTheme();
	const { selectedPolicies, setSelectedPolicies } = usePoliciesSelect();
	const clientRowQuery = useGetClientRowQuery(clientId, { skip: !clientId });
	const clientInsightId = clientRowQuery.data?.insightId;

	const [rowsPerPage, setRowsPerPage] = useState(rowLimits[1]);
	const [page, setPage] = useState(0);
	const [sortModel, setSortModel] = useState([
		{
			field: 'ToDate',
			sort: 'desc',
		},
	]);

	const {
		data: insightPoliciesData,
		fetchNextPage,
		hasNextPage,
		isLoading,
		isError,
		isFetching,
		isUninitialized,
	} = useGetInsightPoliciesInfiniteQuery(
		{
			clientId: clientInsightId,
			...(statusId != null && { statusId }),
			...(typeId != null && { typeId }),
		},
		{ skip: !clientInsightId },
	);

	const insightPages = insightPoliciesData?.pages ?? [];

	useEffect(() => {
		setPage(0);
	}, [clientId]);

	const allPolicies = useMemo(
		() => insightPages.flatMap((insightPage) => insightPage?.Results ?? []),
		[insightPages],
	);
	const pageCount = Math.max(1, Math.ceil(allPolicies.length / rowsPerPage));

	// Load every page up front so sorting covers the client's full policy list
	useEffect(() => {
		if (!hasNextPage || isFetching) {
			return;
		}

		fetchNextPage();
	}, [hasNextPage, isFetching, fetchNextPage]);

	// Sort the full list here; the grid only receives the current page slice
	const sortedPolicies = useMemo(
		() => stableSort(allPolicies, getComparator(sortModel[0].sort, sortModel[0].field, true)),
		[allPolicies, sortModel],
	);

	const displayedRows = useMemo(() => {
		const startIndex = page * rowsPerPage;
		return sortedPolicies.slice(startIndex, startIndex + rowsPerPage);
	}, [sortedPolicies, page, rowsPerPage]);

	const showError = isError || clientRowQuery.isError;
	const loadingState =
		!showError &&
		Boolean(clientId) &&
		(clientRowQuery.isLoading ||
			clientRowQuery.isFetching ||
			(Boolean(clientInsightId) &&
				(isLoading ||
					isUninitialized ||
					(isFetching && (page + 1) * rowsPerPage > allPolicies.length))));

	const handleSelectionChange = (selectedIds) => {
		setSelectedPolicies(
			selectedIds
				.map(
					(id) =>
						allPolicies.find((policy) => policy.Id === id) ??
						selectedPolicies.find((policy) => policy.Id === id),
				)
				.filter(Boolean),
		);
	};

	const TablePagination = useCallback(() => {
		return (
			<InfiniteTablePagination
				count={allPolicies.length}
				page={page}
				setPage={setPage}
				rowsPerPage={rowsPerPage}
				setRowsPerPage={setRowsPerPage}
				rowsPerPageOptions={rowLimits}
				hasNextPage={page + 1 < pageCount || hasNextPage}
				hasPreviousPage={page > 0}
			/>
		);
	}, [allPolicies.length, page, pageCount, rowsPerPage, hasNextPage]);

	const emptyLabel = !clientId
		? 'Select a client to see associated policies.'
		: 'No policies found for this client. Please create in INSIGHT first.';

	return (
		<Box
			className='MuiDataGrid-parentDiv'
			sx={{
				mt: 1,
				border: showError ? '1px solid red' : `1px solid ${theme.palette.divider}`,
				borderRadius: '8px',
				overflow: 'hidden',
				backgroundColor: theme.palette.background.paper,
			}}
		>
			<DataGrid
				autoHeight
				loading={loadingState}
				rows={showError ? [] : displayedRows}
				columns={columns}
				getRowId={(row) => row.Id}
				checkboxSelection
				disableRowSelectionOnClick
				keepNonExistentRowsSelected
				rowSelectionModel={selectedPolicies.map((policy) => policy.Id)}
				onRowSelectionModelChange={handleSelectionChange}
				disableColumnMenu
				sortingMode='server'
				sortingOrder={['desc', 'asc']}
				sortModel={sortModel}
				onSortModelChange={(newSortModel) => {
					setSortModel(newSortModel);
					setPage(0);
				}}
				paginationMode='server'
				rowCount={allPolicies.length}
				paginationModel={{ page, pageSize: rowsPerPage }}
				slots={{
					noRowsOverlay: showError
						? () => <NoRowsErrorOverlay objectType='policies' />
						: () => <NoRowsOverlay objectType='policies' label={emptyLabel} />,
					pagination: TablePagination,
				}}
				slotProps={{
					loadingOverlay: {
						variant: 'skeleton',
						noRowsVariant: 'skeleton',
					},
				}}
				sx={{
					border: 'none',
					backgroundColor: theme.palette.background.paper,
					'& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
						outline: 'none',
					},
					'& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
						outline: 'none',
					},
				}}
			/>
		</Box>
	);
};
